import React, { useCallback, useEffect, useMemo, useState } from "react";
import Layout from "@/components/layout/Layout";
import { ArrowLeft, ChevronRight } from "lucide-react";
import useRoleStore, { PermissionGroup } from "./store/rolesStore";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useShallow } from "zustand/react/shallow";
import useGlobalStore from "@/store/globalStore";
import Button from "@/components/ui/Button";
import Spinner from "@/components/ui/Spinner";
import Checkbox from "@/components/ui/CheckBox";

// ── Group accordion row ───────────────────────────────────────────────────────

interface GroupRowProps {
  group: PermissionGroup;
  isOpen: boolean;
  onToggleOpen: () => void;
  onToggleGroup: () => void;
  onTogglePerm: (permId: string) => void;
}

const GroupRow: React.FC<GroupRowProps> = ({
  group,
  isOpen,
  onToggleOpen,
  onToggleGroup,
  onTogglePerm,
}) => {
  const assignedCount = group.assigned_count;
  const totalCount = group.total_count;

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      {/* Group header */}
      <div
        className="flex items-center gap-3 px-4 py-3 cursor-pointer"
        onClick={onToggleOpen}
      >
        <div onClick={(e) => e.stopPropagation()}>
          <Checkbox
            checked={group.all_assigned}
            onChange={() => onToggleGroup()}
          />
        </div>

        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-900 truncate">
            {group.display_name}
          </p>
          {group.description && (
            <p className="text-xs text-gray-400 truncate">
              {group.description}
            </p>
          )}
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          <span
            className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
              group.all_assigned
                ? "bg-green-100 text-green-700"
                : group.some_assigned
                  ? "bg-purple-100 text-purple-700"
                  : "bg-gray-100 text-gray-500"
            }`}
          >
            {group.all_assigned
              ? "All assigned"
              : group.some_assigned
                ? "Partial"
                : "None"}
          </span>
          <span className="text-xs text-gray-400">
            {assignedCount}/{totalCount}
          </span>
        </div>

        <ChevronRight
          className={`w-4 h-4 text-gray-400 transition-transform flex-shrink-0 ${isOpen ? "rotate-90" : ""}`}
        />
      </div>

      {/* Expandable permission rows */}
      {isOpen && (
        <div className="border-t border-gray-200">
          {group.permissions.map((perm) => (
            <div
              key={perm.id}
              className="flex items-center gap-3 px-4 py-2.5 pl-12 border-b border-gray-100 last:border-b-0"
            >
              <Checkbox
                checked={perm.assigned}
                onChange={() => onTogglePerm(perm.id)}
              />
              <div className="min-w-0">
                <p className="text-sm text-gray-800">{perm.label ?? perm.name}</p>
                {perm.desc && (
                  <p className="text-xs text-gray-400">{perm.desc}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Main page ─────────────────────────────────────────────────────────────────

const RolePermissions: React.FC = () => {
  const [searchParams] = useSearchParams();
  const roleId = searchParams.get("id");
  const navigate = useNavigate();

  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<string>("all");
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [localGroups, setLocalGroups] = useState<PermissionGroup[]>([]);

  const {
    getPermissions,
    setRoleById,
    getRoles,
    permissionGroups,
    sidebar,
    selectedRole,
    savePermissions,
    setPermissions,
  } = useRoleStore(
    useShallow((s) => ({
      getPermissions: s.getPermissions,
      setRoleById: s.setRoleById,
      getRoles: s.getRoles,
      permissionGroups: s.permissionGroups,
      sidebar: s.sidebar,
      selectedRole: s.selectedRole,
      savePermissions: s.savePermissions,
      setPermissions: s.setPermissions,
    })),
  );

  const setError = useGlobalStore((s) => s.setError);
  const showSuccess = useGlobalStore((s) => s.showSuccess);

  const load = useCallback(async () => {
    if (!selectedRole && roleId) {
      try {
        await getRoles();
        setRoleById(roleId);
      } catch (e) {
        setError(e instanceof Error ? e.message : "Something went wrong");
        return;
      }
    }
    try {
      await getPermissions(roleId ?? undefined);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Something went wrong");
    }
  }, [selectedRole, roleId]);

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    if (permissionGroups)
      setLocalGroups(
        permissionGroups.map((g) => ({
          ...g,
          permissions: g.permissions.map((p) => ({ ...p })),
        })),
      );
  }, [permissionGroups]);

  const tabs = useMemo(
    () => [
      { key: "all", label: "All", groupNames: null as string[] | null },
      ...(sidebar ?? []).map((s) => ({
        key: s.key,
        label: s.display_name,
        groupNames: s.permission_groups.map((g) => g.name),
      })),
    ],
    [sidebar],
  );

  const visibleGroups = useMemo(() => {
    const tab = tabs.find((t) => t.key === activeTab);
    if (!tab || !tab.groupNames) return localGroups;
    const allowed = new Set(tab.groupNames);
    return localGroups.filter((g) => allowed.has(g.name));
  }, [tabs, activeTab, localGroups]);

  const recompute = (groups: PermissionGroup[]): PermissionGroup[] =>
    groups.map((g) => {
      const total = g.permissions.length;
      const assigned = g.permissions.filter((p) => p.assigned).length;
      return {
        ...g,
        assigned_count: assigned,
        all_assigned: assigned === total,
        some_assigned: assigned > 0 && assigned < total,
        none_assigned: assigned === 0,
      };
    });

  const toggleGroup = (groupId: string) => {
    setLocalGroups((prev) => {
      const updated = prev.map((g) => {
        if (g.id !== groupId) return g;
        const newVal = !g.all_assigned;
        return {
          ...g,
          permissions: g.permissions.map((p) => ({ ...p, assigned: newVal })),
        };
      });
      return recompute(updated);
    });
  };

  const togglePerm = (groupId: string, permId: string) => {
    setLocalGroups((prev) => {
      const updated = prev.map((g) => {
        if (g.id !== groupId) return g;
        return {
          ...g,
          permissions: g.permissions.map((p) =>
            p.id === permId ? { ...p, assigned: !p.assigned } : p,
          ),
        };
      });
      return recompute(updated);
    });
  };

  return (
    <Layout>
      <h1 className="text-2xl font-bold mb-8">Edit Permissions</h1>

      {!localGroups.length ? (
        <div className="flex justify-center items-center py-20">
          <Spinner size={30} />
        </div>
      ) : (
        <>
          {/* Role header */}
          <div className="flex items-center gap-3 mb-6">
            <button
              onClick={() => {
                setPermissions(null);
                navigate(-1);
              }}
              className="flex items-center justify-center w-8 h-8 rounded-full hover:bg-gray-100 transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-[#EA33C0]" />
            </button>
            <h2 className="text-xl font-semibold">
              {selectedRole?.name ?? "N/A"}
            </h2>
            {selectedRole?.scope && (
              <span
                className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                  selectedRole.scope === "global"
                    ? "bg-purple-100 text-purple-700"
                    : "bg-emerald-100 text-emerald-700"
                }`}
              >
                {selectedRole.scope.charAt(0).toUpperCase() +
                  selectedRole.scope.slice(1)}
              </span>
            )}
          </div>

          {/* Tabs */}
          <div className="flex gap-1 mb-6 border-b border-gray-300 flex-wrap">
            {tabs.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setActiveTab(t.key)}
                className={`text-sm px-4 py-2 transition-colors relative
                  ${
                    activeTab === t.key
                      ? "text-[#EA33C0] font-semibold border-b-2 border-[#EA33C0]"
                      : "text-gray-400 hover:text-gray-700"
                  }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {/* Permission groups */}
          <div className="flex flex-col gap-3 mb-8">
            {visibleGroups.length === 0 ? (
              <p className="text-sm text-gray-400 py-6 text-center">
                No permission groups in this section.
              </p>
            ) : (
              visibleGroups.map((group) => (
                <GroupRow
                  key={group.id}
                  group={group}
                  isOpen={!!expanded[group.id]}
                  onToggleOpen={() =>
                    setExpanded((p) => ({ ...p, [group.id]: !p[group.id] }))
                  }
                  onToggleGroup={() => toggleGroup(group.id)}
                  onTogglePerm={(permId) => togglePerm(group.id, permId)}
                />
              ))
            )}
          </div>

          <div className="flex">
            <Button
              type="primary"
              isLoading={isSaving}
              onClick={async () => {
                try {
                  setIsSaving(true);
                  await savePermissions(localGroups);
                  showSuccess("Permissions saved successfully");
                } catch (e) {
                  setError(
                    e instanceof Error ? e.message : "Something went wrong",
                  );
                } finally {
                  setIsSaving(false);
                }
              }}
            >
              Save Changes
            </Button>
          </div>
        </>
      )}
    </Layout>
  );
};

export default RolePermissions;
